import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { useState, useMemo, useEffect, useRef } from "react";
import { useCompConfig } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import TiLeMatch from "@/components/TiLeMatch";
import LoadingInner from "@/components/Loading";
import NoData from "@/components/NoData";
import MatchLeagues from "@/components/MatchLeagues";
import HotLeagueTabs from "@/components/HotLeagueTabs";
import TeamSearchInput from "@/components/TeamSearchInput";
import LeagueSearchDropdown from "@/components/LeagueSearchDropdown";
import useHotLeagues from "@/hooks/useHotLeagues";
import useAllLeagues from "@/hooks/useAllLeagues";
import { BANG_KEO_PAGE_LIMIT } from "@/contants";
import Carousel from "@/components/Carousel";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import FinanceeMatchLeagues from "../FinanceeMatchLeagues";
import FinanceeMatch from "../FinanceeMatch";
import useFavoriteMatches from "@/hooks/useFavoriteMatches";
import { useSelector } from "react-redux";
import KbvHeadTableOdds from "../KbvHeadTableOdds";
import KbvFinanceeDateRange,  { FAVORITE_VALUE } from "../FinanceeDateRange";


dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";


const KbvBangKeo = ({ type = "all" }) => {
  const {
    colorPrimary,
    colorSecondary,
    colorBg,
    colorText,
    colorForeground,
    colorOnPrimary,
    colorBorder,
    colorTextMuted,
    colorAccent,
    colorHeadTableGradientStart,
    colorHeadTableGradientMid,
    colorBookmakerTriggerGradientStart,
    colorBookmakerTriggerGradientEnd,
    hasDataConfig,
  } = useCompConfig();
  const [firstLoad, setFirstLoad] = useState(true);
  const [activeLeague, setActiveLeague] = useState(null);



  const today = dayjs().tz(TZ).format("YYYY-MM-DD");
  const [selectedDateValue, setSelectedDateValue] = useState('live');
  const [searchQuery, setSearchQuery] = useState("");
  const { clearAllData } = useLiveData();
  const { leagues } = useHotLeagues({ limit: 10 });
  const { leagues: allLeagues } = useAllLeagues();

  //favorite
  const isFavorite = selectedDateValue === FAVORITE_VALUE;
  const favoriteMatches = useSelector((state) => state.favoriteReducer?.favoriteMatches ?? []);
  const bookmarker = useSelector((state) => state.bookmakerReducer);

  const dateString =
    selectedDateValue === "live" ? today : isFavorite ? today : selectedDateValue;
  const status =
    type === "ketqua" ? "end" : selectedDateValue === "live" ? "live" : "";

  const queryObject = useMemo(() => {
    let filterInclude;
    if (activeLeague?.id && selectedDateValue) {
      filterInclude = `leagueId:${activeLeague.id},date:${dateString}`;
    } else if (activeLeague?.id) {
      filterInclude = `leagueId:${activeLeague.id}`;
    } else {
      filterInclude = `date:${dateString}`;
    }
    return {
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude,
      filterOdds: -1,
      cache: false,
      status,
      page: 1,
      limit: BANG_KEO_PAGE_LIMIT,
    };
  }, [activeLeague?.id, dateString, status, selectedDateValue]);

  // query fetch favorite
  const snapshotIdsRef = useRef("");
  const prevIsFavoriteRef = useRef(false);
  if (isFavorite && !prevIsFavoriteRef.current) {
    snapshotIdsRef.current = favoriteMatches.map((m) => m.id).join(",");
  }
  prevIsFavoriteRef.current = isFavorite;

  const favoriteQueryObject = useMemo(
    () =>
      isFavorite && snapshotIdsRef.current
        ? {
            matchIds: snapshotIdsRef.current,
            include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
            filterOdds: -1,
            page: 1,
            limit: BANG_KEO_PAGE_LIMIT,
          }
        : null,
    [isFavorite]
  );


  const bangKeo = useBangKeoMatches(queryObject);
  const favorite = useFavoriteMatches(favoriteQueryObject);

  const themeStyle = hasDataConfig
    ? {
        "--theme-primary": colorPrimary,
        "--theme-highlight": colorSecondary,
        "--theme-bg": colorBg,
        "--theme-text": colorText,
        "--theme-foreground": colorForeground,
        "--theme-on-primary": colorOnPrimary,
        "--theme-border": colorBorder,
        "--theme-btn-border": colorBorder,
        "--theme-text-muted": colorTextMuted,
        "--theme-accent": colorAccent,
        ...(colorHeadTableGradientStart != null && { "--theme-head-table-gradient-start": colorHeadTableGradientStart }),
        ...(colorHeadTableGradientMid != null && { "--theme-head-table-gradient-mid": colorHeadTableGradientMid }),
        ...(colorBookmakerTriggerGradientStart != null && { "--theme-bookmaker-trigger-gradient-start": colorBookmakerTriggerGradientStart }),
        ...(colorBookmakerTriggerGradientEnd != null && { "--theme-bookmaker-trigger-gradient-end": colorBookmakerTriggerGradientEnd }),
      }
    : undefined;

  const leaguesWithMatches = isFavorite ? favorite.leaguesWithMatches : bangKeo.leaguesWithMatches;
  const loading = isFavorite ? favorite.loading : bangKeo.loading;
  const { hasMore, loadMoreRef, isLoadingMoreRef } = bangKeo;


  const filteredLeaguesWithMatches = useMemo(() => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return leaguesWithMatches;
    return leaguesWithMatches
      .map(({ league, matches }) => {
        const filtered = (matches || []).filter((match) => {
          const home = (match.homeName || match.homeTeam?.name || "").toLowerCase();
          const away = (match.awayName || match.awayTeam?.name || "").toLowerCase();
          return home.includes(q) || away.includes(q);
        });
        return { league, matches: filtered };
      })
      .filter(({ matches }) => matches.length > 0);
  }, [leaguesWithMatches, searchQuery]);

  const matchIds = useMemo(
    () =>
      filteredLeaguesWithMatches.flatMap(
        (league) => league.matches?.map((match) => match.id) || [],
      ),
    [filteredLeaguesWithMatches],
  );

  // live updates for all match IDs
  useLiveUpdates(matchIds);
  // clear match data live 
  useEffect(() => {
    return () => {
      clearAllData();
    };
  }, [clearAllData]);

  useEffect(() => {
    if (firstLoad && selectedDateValue === 'live' && !loading && leaguesWithMatches.length <= 0) {
      setSelectedDateValue(today);
    }
  }, [loading, leaguesWithMatches, today, selectedDateValue]);

  const handleDateChange = (value) => {
    setSelectedDateValue(value);
    setFirstLoad(false);
  };

  const handleLeagueChange = (league) => {
    setActiveLeague(league ?? null);
    if (league != null) setSelectedDateValue("");
  };

  return (
    <div
      className="mx-auto px-2 py-3 rounded-xl flex flex-col gap-2"
      style={themeStyle}
    >
      <HotLeagueTabs
        leagues={leagues}
        activeLeagueId={activeLeague?.id}
        onChange={handleLeagueChange}
        autoSelectFirst={false}
      />

      <div className="flex flex-col sm:flex-row gap-2 w-full">
        <div className="flex-1 min-w-0">
          <TeamSearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Tìm đội bóng theo tên"
          />
        </div>
        <div className="w-full sm:w-auto sm:min-w-[200px]">
          <LeagueSearchDropdown
            leagues={allLeagues}
            onSelect={handleLeagueChange}
            placeholder="Tất cả giải đấu"
            showAllLeaguesOption
            selectedLeague={activeLeague}
          />
        </div>
      </div>

      <KbvFinanceeDateRange
        value={activeLeague && !selectedDateValue ? "" : selectedDateValue}
        onChange={handleDateChange}
        showBookmakerSelect={true}
      />

      <KbvHeadTableOdds />

      {loading && <LoadingInner />}
      {
        !loading && (
          <>
            <div className="space-y-2 rounded-lg">
              {
                filteredLeaguesWithMatches.length === 0 ? (
                  <NoData message={searchQuery.trim() ? "Không tìm thấy trận đấu nào" : "Không có trận đấu nào"} />

                ) : (
                  filteredLeaguesWithMatches.map(({ league, matches }) => (
                    <FinanceeMatchLeagues key={league.id} league={league} matches={matches} render={(match) => <FinanceeMatch key={`${match.id}-${bookmarker}`} match={match} />} />
                  ))
                )

              }
            </div>
            {hasMore && (
              <div ref={loadMoreRef} className="py-4 text-center">
                {isLoadingMoreRef && <LoadingInner />}
              </div>
            )}
          </>
        )
      }
    </div>
  );
};

export default KbvBangKeo;